import React from "react";
import C3 from "c3";
import "c3/c3.min.css";
import * as d3 from "d3";
import { ChartList, AnyType } from "../../../../../common/constants";
import { IProtRow } from "../../../../../webapi/service/getFIles";
import { ChartRowDataArray } from "../../../../../webapi/constants";
import ZoomTargetImage from "../../../../../icon/img/chart-zoom-target.png";
import { ISimulationResultsRow } from "../../../../../webapi/service/getSimulationResults";

export type XRangeValue = AnyType;

/**
 * チャートを描画します
 * @param {string} title - タイトル
 * @param {string} chartid - チャートID
 * @param {number} width - 幅
 * @param {number} height - 高さ
 * @param {C3.PrimitiveArray[]} rows - チャートデータ
 * @param {number} maxX - X軸の最大値
 * @returns C3.ChartAPI
 */
export function drawChart(
  title: string,
  chartid: string,
  width: number,
  height: number,
  rows: C3.PrimitiveArray[],
  maxX: number
): C3.ChartAPI {
  console.time(title);
  const c3 = C3.generate({
    title: { text: title },
    bindto: "#" + chartid,
    size: { width: width, height: height },
    data: { x: "x", rows: rows, type: "line" },
    axis: {
      x: {
        tick: { values: [0, maxX] },
        label: { 
          text: ChartList.LABEL_TEXT_X, 
          position: ChartList.LABEL_POSITION 
        },
      },
    },
    grid: {
      x: { show: ChartList.GRID_CHARTLIST },
      y: { show: ChartList.GRID_CHARTLIST },
    },
    padding: { right: width / 15, left: width / 7 },
    point: {
      show: false,
    },
    legend: {
      show: ChartList.LEGEND_SHOW,
      position: ChartList.LEGEND_POSITION,
      inset: { anchor: ChartList.LEGEND_ANCHOR },
    },
    tooltip: {
      show: ChartList.TOOLTIP_SHOW,
      grouped: ChartList.TOOLTIP_GROUPED,
    },
  });
  console.timeEnd(title);
  return c3;
}

/**
 * 複数チャートを描画します
 * @param {IProtRow[]} prots - チャートデータ
 * @param {string} chartidFmt - チャートIDフォーマット
 * @param {number} width - 幅
 * @param {number} height - 高さ
 * @returns C3.ChartAPI 配列
 */
export function drawCharts(
  prots: IProtRow[],
  chartidFmt: string,
  width: number,
  height: number
): C3.ChartAPI[] {
  console.time("drawCharts");
  const charts = prots.map((prot) => {
    const { fname, id, output, input, datanum, interval } = prot;
    const maxX = datanum * interval / 1000;
    if(chartidFmt===ChartList.ID_OUTPUT) {
      return drawChart(
        fname,
        chartidFmt + String(id),
        width,
        height,
        output,
        maxX
      );
    } else {
      return drawChart(
        "",
        chartidFmt + String(id),
        width,
        height,
        input,
        maxX
      );
    }
  });
  console.timeEnd("drawCharts");
  return charts;
}

/**
 * シミュレータのシミュレーション結果のチャートを描画します
 * @param {ISimulationResultsRow[]} simRows - チャートデータ
 * @param {string} chartidFmt - チャートIDフォーマット
 * @param {number} width - 幅
 * @param {number} height - 高さ
 * @returns C3.ChartAPI 配列
 */
 export function drawResultsCharts( 
  simRows: ISimulationResultsRow[],
  chartidFmt: string,
  width: number,
  height: number,
): C3.ChartAPI[] {
  console.time("drawCharts");
  const charts = simRows.map((prot) => {
    const { type, comparisonfile, inrows, outrows, id, simdatanum, interval} = prot;
    const maxX = simdatanum * interval / 1000;
    const title = type === "compare" ?
      comparisonfile : type;
    if(chartidFmt===ChartList.ID_OUTPUT) {
      return drawChart(
        title,
        chartidFmt + String(id),
        width,
        height,
        outrows,
        maxX,
      );
    } else {
      return drawChart(
        "",
        chartidFmt + String(id),
        width,
        height,
        inrows,
        maxX,
      );
    }
  });
  console.timeEnd("drawCharts");
  return charts;
}

/**
 * チャートサイズ
 */
export type ChartSize = {
  /**
   * 幅
   */
  width: number;
  /**
   * 高さ
   */
  height: number;
};

/**
 * チャートテーブル情報
 */
export type ChartTableInfo = {
  /**
   * チャートテーブル幅
   */
  tableWidth: number;
  /**
   * チャート幅
   */
  chartWidth: number;
  /**
   * チャート高さ
   */
  chartHeight: number;
};

/**
 * チャートテーブル情報を取得します。
 * チャートをリサイズする場合は横並び数を切り捨て、しない場合は四捨五入
 * @param {number} width - 表示幅
 * @param {number} marginLeft - チャートテーブルLeft Margin
 * @param {ChartSize} chartSize - 初期チャートサイズ
 * @param {boolean} resizeEnabled - チャートをリサイズする場合はtrue
 * @param {number} chartCount - チャート数
 * @returns チャートテーブル情報
 */
export function getChartTableInfo(
  width: number,
  chartSize: ChartSize,
  resizeEnabled: boolean,
  chartCount?: number
): ChartTableInfo {
  const marginLeft = ChartList.TABLE_MARGIN_LEFT;
  const chartMargin = 2; // projectdetail.css -> chart-list-item -> borderと合わせる
  let chartWidth = chartSize.width;
  let chartHeight = chartSize.height;
  let chartTableWidth = chartWidth + chartMargin;
  const math = resizeEnabled ? Math.floor : Math.round;
  let count = math((width - marginLeft) / (chartSize.width + chartMargin));
  if (count > 0) {
    if (resizeEnabled) {
      chartWidth = Math.floor((width - marginLeft) / count - chartMargin);
      chartWidth = chartWidth < chartSize.width ? chartSize.width : chartWidth;
      chartHeight = Math.floor((chartWidth * chartSize.height) / chartWidth);
      chartHeight =
        chartHeight < chartSize.height ? chartSize.height : chartHeight;
    }
    count =
      typeof chartCount !== "undefined" && chartCount < count
        ? chartCount
        : count;
    chartTableWidth = count * (chartWidth + chartMargin);
  }
  return {
    tableWidth: chartTableWidth,
    chartWidth: chartWidth,
    chartHeight: chartHeight,
  };
}

/**
 * C3チャートIDを取得します
 * @param {string} chartid - チャートID
 * @returns C3チャートID
 */
export function C3ChartID(chartid: string): string {
  return "#" + chartid;
}

/**
 * C3で作成したSVG検索文字列を取得します
 * @param {string} c3chartid - C3チャートID
 * @returns SVG検索文字列
 */
export function SVGSelecter(c3chartid: string): string {
  return c3chartid + " svg";
}

/**
 * C3チャートの点表示を切り替えます
 * @param {C3.ChartAPI} c3 - C3.ChartAPI
 * @param {boolean} display - 点を表示する場合はtrue,それ以外はfalse
 */
export function displayChartPoint(c3: C3.ChartAPI, display: boolean): void {
  const c3i = c3.internal;
  c3i.main
    .selectAll(".c3-circles")
    .style("display", display === false ? "none" : "initial");
  c3i.eventRect.style("cursor", display === false ? "none" : "pointer");
}

/**
 * RootのElementを取得します
 * @param {C3.ChartAPI} c3 - C3.ChartAPI
 */
export function getRootElement(c3: C3.ChartAPI) {
  return c3.internal.main.select("g.c3-chart");
}

const InsertTarget = "g.c3-event-rects";
const RangeTag = "rect";
const RangeClass = "chart-range";
const RangeSelecter = RangeTag + "." + RangeClass;

/**
 * チャートに範囲指定を追加します
 * @param {C3.ChartAPI} c3 - C3.ChartAPI
 * @param {number} height - 高さ
 */
export function insertRangeElement(c3: C3.ChartAPI, height: number): void {
  getRootElement(c3)
    .insert(RangeTag, InsertTarget)
    .attr("class", RangeClass)
    .attr("x", 0)
    .attr("y", 0)
    .attr("width", 0)
    .attr("height", height)
    .style("fill", "#c0c0ff")
    .style("stroke-width", 0)
    .style("fill-opacity", "25%")
    .attr("base", 0)
    .attr("start", 0)
    .attr("end", 0);
}

/**
 * チャートの範囲指定を取得します
 * @param {C3.ChartAPI} c3 - C3.ChartAPI
 * @returns 範囲指定
 */
export function getRangeElement(c3: C3.ChartAPI) {
  return getRootElement(c3).select(RangeSelecter);
}

/**
 * チャートの範囲指定を初期化します
 * @param {C3.ChartAPI} c3 - C3.ChartAPI
 * @param {number} height - 高さ
 */
export function initRangeElement(c3: C3.ChartAPI, height: number): void {
  getRangeElement(c3)
    .attr("x", 0)
    .attr("width", 0)
    .attr("height", height)
    .attr("base", 0)
    .attr("start", 0)
    .attr("end", 0);
}

/**
 * チャートの範囲指定をリサイズします
 * @param {C3.ChartAPI} c3 - C3.ChartAPI
 * @param {number} height - 高さ
 */
function resizeRangeElement(c3: C3.ChartAPI, height: number): void {
  getRangeElement(c3).attr("height", height);
}

/**
 * チャートの範囲指定をZoomします
 * @param {C3.ChartAPI} c3 - C3.ChartAPI
 */
function zoomRangeElement(c3: C3.ChartAPI): void {
  const range = getRangeElement(c3);
  if (!range.empty()) {
    const c3i = c3.internal;
    const startX = c3i.x(parseInt(range.attr("start")));
    const endX = c3i.x(parseInt(range.attr("end")));
    range.attr("x", startX);
    range.attr("width", endX - startX);
  }
}

/**
 * C3チャートX軸の値を配列で取得します
 * @param {C3.ChartAPI} c3i - C3.ChartAPI
 * @returns X軸値配列
 */
export function getAxisXValues(c3: C3.ChartAPI): number[] {
  return c3.data()[0].values.map((val) => {
    return val.x;
  });
}

/**
 * 範囲選択の選択中X座標を取得します
 * @param c3i - C3
 * @param xs - x値配列
 * @param mouseX - マウスX座標
 * @returns
 */
function getSelectedX(
  c3i: C3.ChartInternal,
  xs: number[],
  mouseX: number
): number {
  let minAbs = Infinity;
  let index = 0;
  xs.forEach((x) => {
    const xpos = c3i.x(x);
    const abs = Math.abs(xpos - mouseX);
    if (abs < minAbs) {
      minAbs = abs;
      index = x;
    }
  });
  return index;
}

/**
 * 範囲選択のX軸範囲を取得します
 * @param c3i - C3
 * @param xs - x値配列
 * @param baseX - 範囲選択開始X座標
 * @param mouseX - マウスX座標
 * @returns X軸範囲
 */
function getAxisXRange(
  c3i: C3.ChartInternal,
  xs: number[],
  baseX: XRangeValue,
  mouseX: number
): { start: number; end: number } {
  const width = mouseX < baseX ? baseX - mouseX : mouseX - baseX;
  const startX = mouseX < baseX ? mouseX : baseX;
  const endX = startX + width;
  let sminAbs = Infinity;
  let eminAbs = Infinity;
  let sindex = 0;
  let eindex = 0;
  xs.forEach((x) => {
    const xpos = c3i.x(x);
    const sabs = Math.abs(xpos - startX);
    if (sabs < sminAbs) {
      sminAbs = sabs;
      sindex = x;
    }
    const eabs = Math.abs(xpos - endX);
    if (eabs < eminAbs) {
      eminAbs = eabs;
      eindex = x;
    }
  });
  return { start: sindex, end: eindex };
}

/**
 * 範囲選択を開始します
 * @param {C3.ChartAPI} c3 - C3.ChartInternal
 * @param {number[]} xs - X軸値配列
 * @param {number} mouseX - マウスX座標
 * @param {(x: number) => void} callback - コールバック
 */
export function startRangeSelection(
  c3: C3.ChartAPI,
  xs: number[],
  mouseX: number,
  callback?: (x: number) => void
): void {
  const c3i = c3.internal;
  const x = getSelectedX(c3i, xs, mouseX);
  getRangeElement(c3)
    .attr("x", c3i.x(x))
    .attr("width", 0)
    .attr("base", x)
    .attr("start", x)
    .attr("end", x);
  if (callback) {
    callback(x);
  }
}

/**
 * 範囲選択を設定します
 * @param {C3.ChartAPI} c3 - C3.ChartAPI
 * @param {number[]} xs - X軸値配列
 * @param {number} mouseX - マウスX座標
 * @param {(start: number, end: number) => void} callback - コールバック
 */
export function setRangeSelection(
  c3: C3.ChartAPI,
  xs: number[],
  mouseX: number,
  callback?: (start: number, end: number) => void
): void {
  const c3i = c3.internal;
  const range = getRangeElement(c3);
  const baseX = c3i.x(parseInt(range.attr("base")));
  const { start, end } = getAxisXRange(c3i, xs, baseX, mouseX);
  const sx = c3i.x(start);
  const ex = c3i.x(end);
  range
    .attr("x", sx)
    .attr("width", ex - sx)
    .attr("start", start)
    .attr("end", end);
  if (callback) {
    callback(start, end);
  }
}

const ZoomTargetTag = "image";
const ZoomTargetClass = "zoom-target";
const ZoomTargetSelecter = ZoomTargetTag + "." + ZoomTargetClass;
const ZoomTargetSize = 24;
const ZoomTargetCorrection = ZoomTargetSize / 2;

/**
 * チャートの中央マウス座標を取得します
 * @param c3 - C3
 * @returns 中央マウス座標
 */
function getCenter(c3: C3.ChartAPI) {
  const { min, max } = c3.axis.range();
  const c3i = c3.internal;
  const x = c3i.x((max.x - min.x) / 2);
  const y = c3i.y((max.y - min.y) / 2 + min.y);
  return { x: x, y: y };
}

/**
 * Zoomターゲット位置の補正値を取得します
 * @param pos - Zoomターゲット位置
 * @returns Zoomターゲット位置補正値
 */
function getPosValue(pos: number) {
  return Math.round(pos) - ZoomTargetCorrection;
}

/**
 * チャートにZoomターゲットを追加します
 * @param {C3.ChartAPI} c3 - C3.ChartAPI
 */
export function insertZoomTarget(c3: C3.ChartAPI): void {
  const { x, y } = getCenter(c3);
  getRootElement(c3)
    .insert(ZoomTargetTag, InsertTarget)
    .attr("class", ZoomTargetClass)
    .attr("x", getPosValue(x))
    .attr("y", getPosValue(y))
    .attr("width", ZoomTargetSize)
    .attr("height", ZoomTargetSize)
    .attr("xlink:href", ZoomTargetImage);
}

/**
 * チャートのZoomターゲットを取得します
 * @param {C3.ChartAPI} c3 - C3.ChartAPI
 * @returns Zoomターゲット
 */
export function getZoomTarget(c3: C3.ChartAPI) {
  return getRootElement(c3).select(ZoomTargetSelecter);
}

/**
 * チャートのZoomターゲットを設定します
 * @param {C3.ChartAPI} c3 - C3.ChartAPI
 * @param {number} x - X座標
 * @param {number} y - Y座標
 */
export function setZoomTarget(c3: C3.ChartAPI, x: number, y: number): void {
  getZoomTarget(c3).attr("x", getPosValue(x)).attr("y", getPosValue(y));
}

/**
 * チャートのZoomターゲットを初期化します
 * @param {C3.ChartAPI} c3 - C3.ChartAPI
 */
export function initZoomTarget(c3: C3.ChartAPI): void {
  const { x, y } = getCenter(c3);
  setZoomTarget(c3, x, y);
}

/**
 * 軸範囲
 */
export type AxisRange = {
  /**
   * 最小値
   */
  min: number;
  /**
   * 最大値
   */
  max: number;
};

/**
 * マウスホイール移動量補正を取得します
 * @param datanum - データ点数
 * @returns 移動量補正
 */
function getCorrection(datanum: number) {
  const digit = String(datanum).length;
  let exponent = 0;
  if (digit <= 3) {
    exponent = 2;
  } else if (digit === 4) {
    exponent = 1;
  }
  return Math.pow(10, exponent);
}

/**
 * マウスホイールイベントを設定します
 * @param {C3.ChartAPI} c3 - C3.ChartAPI
 * @param {AxisRange} range - 移動可能X軸範囲
 * @param {number} datanum - データ数
 */
export function setMousewheel(
  c3: C3.ChartAPI,
  range: AxisRange,
  datanum: number
): void {
  const { min, max } = range;
  const correction = getCorrection(datanum);
  getRootElement(c3).on("mousewheel", function () {
    const event = d3.event;
    const move = event.wheelDelta / correction;
    const range = c3.axis.range();
    let start = range.min.x + move;
    let end = range.max.x + move;
    if (start < min) {
      start = min - 1;
      end = start + (range.max.x - range.min.x);
    } else if (end > max) {
      end = max + 1;
      start = end - (range.max.x - range.min.x);
    }
    c3.axis.range({
      min: { x: start },
      max: { x: end },
    });
    zoomRangeElement(c3);
  });
}

/**
 * チャートのX軸範囲を設定します
 * @param {C3.ChartAPI} c3 - C3.ChartAPI
 * @param {number} rate - 拡大率
 * @param {AxisRange} range - X軸範囲
 */
export function setXAxisRange(
  c3: C3.ChartAPI,
  rate: number,
  range: AxisRange
): void {
  const { min, max } = range;
  const x = parseInt(getZoomTarget(c3).attr("x"));
  const baseX = Math.round(c3.internal.x.invert(x));
  const left = (baseX - min) * rate;
  const right = (max - baseX) * rate;
  c3.axis.range({
    min: { x: min + left },
    max: { x: max - right },
  });
  zoomRangeElement(c3);
}

/**
 * Y軸の値を丸めます
 * @param value - Y軸値
 * @returns Y軸値
 */
 export function roundYAxisValue(value: number) {
  return Number(value.toFixed(5));
}

/**
 * チャートのY軸範囲を設定します
 * @param {C3.ChartAPI} c3 - C3.ChartAPI
 * @param {number} rate - 拡大率
 * @param {AxisRange} range - Y軸範囲
 */
export function setYAxisRange(
  c3: C3.ChartAPI,
  rate: number,
  range: AxisRange
): void {
  const { min, max } = range;
  const y = parseInt(getZoomTarget(c3).attr("y"));
  const baseY = c3.internal.y.invert(y);
  const bottom = (baseY - min) * rate;
  const top = (max - baseY) * rate;
  c3.axis.range({
    min: { y: roundYAxisValue(min + bottom) },
    max: { y: roundYAxisValue(max - top) },
  });
}

export type ChartDataArray = AnyType[];

/**
 * 2次元配列の方向を変更します
 * @param {ChartDataArray} data - 2次元配列
 * @returns 2次元配列
 */
export function transpose(data: ChartDataArray): ChartRowDataArray {
  return Object.keys(data[0]).map(function (c) {
    return data.map(function (r) {
      return r[c];
    });
  });
}

/**
 * チャートをリサイズします
 * @param {C3.ChartAPI} c3 - C3.ChartAPI
 * @param {number} width - 幅
 * @param {number} height - 高さ
 * @param {(size?: ChartSize) => ChartSize} chartSize - チャートサイズ
 */
export function resizeChart(
  c3: C3.ChartAPI,
  width: number,
  height: number,
  chartSize: (size?: ChartSize) => ChartSize
) {
  const size = chartSize();
  if (size.width !== width && size.height !== height) {
    console.time("resizeChart");
    c3.resize({ width: width, height: height });
    console.timeEnd("resizeChart");
    chartSize({ width: width, height: height });
    resizeRangeElement(c3, height);
    setZoomTarget(c3, getPosValue( width / 2 ), getPosValue( height / 2));
  } else if (size.width !== width) {
    console.time("resizeChart");
    c3.resize({ width: width });
    console.timeEnd("resizeChart");
    chartSize({ width: width, height: height });
    setZoomTarget(c3, getPosValue( width / 2 ), getPosValue( height / 2));
  } else if (size.height !== height) {
    console.time("resizeChart");
    c3.resize({ height: height });
    console.timeEnd("resizeChart");
    chartSize({ width: width, height: height });
    resizeRangeElement(c3, height);
    setZoomTarget(c3, getPosValue( width / 2 ), getPosValue( height / 2));
  }
  zoomRangeElement(c3);
}

/**
 * チャートをリサイズします
 * @param {C3.ChartAPI[]} c3 - C3.ChartAPI配列
 * @param {number} width - 幅
 * @param {number} height - 高さ
 * @param {(size?: ChartSize) => ChartSize} chartSize - チャートサイズ
 */
function resizeCharts(
  charts: C3.ChartAPI[],
  width: number,
  height: number,
  chartSize: (size?: ChartSize) => ChartSize
): void {
  const size = chartSize();
  if (size.width !== width && size.height !== height) {
    console.time("resizeCharts");
    charts.forEach((chart) => {
      chart.resize({ width: width, height: height });
    });
    console.timeEnd("resizeCharts");
    chartSize({ width: width, height: height });
  } else if (size.width !== width) {
    console.time("resizeCharts");
    charts.forEach((chart) => {
      chart.resize({ width: width });
    });
    console.timeEnd("resizeCharts");
    chartSize({ width: width, height: height });
  } else if (size.height !== height) {
    console.time("resizeCharts");
    charts.forEach((chart) => {
      chart.resize({ height: height });
    });
    console.timeEnd("resizeCharts");
    chartSize({ width: width, height: height });
  }
}

/**
 * チャートテーブルスタイルを作成します
 * @param {number} width - 表示幅
 * @param {ChartSize} size - 初期チャートサイズ
 * @param {C3.ChartAPI[]} charts - C3.ChartAPI配列
 * @param {(size?: ChartSize) => ChartSize} chartSize - チャートサイズ
 * @returns チャートテーブルスタイル
 */
export function createChartTableStyle(
  width: number,
  size: ChartSize,
  charts: C3.ChartAPI[] | null,
  chartSize: (size?: ChartSize) => ChartSize
): React.CSSProperties {
  const chartResizeEnabled = ChartList.RESIZE_ENABLED;
  const { tableWidth, chartWidth, chartHeight } = getChartTableInfo(
    width,
    chartResizeEnabled ? size : chartSize(),
    chartResizeEnabled,
    charts ? charts.length : undefined
  );
  const chartTableStyle: React.CSSProperties = {
    width: tableWidth,
    marginLeft: ChartList.TABLE_MARGIN_LEFT,
  };
  if (chartResizeEnabled && charts && charts.length > 0) {
    resizeCharts(charts, chartWidth, chartHeight, chartSize);
  }
  return chartTableStyle;
}

/**
 * チャート一覧をソートします
 * @param prots - チャート行データ
 * @param sortIds - ソート順ID配列
 * @returns チャート行データ
 */
export function getSortProts(prots: IProtRow[], sortIds: number[]) {
  return sortIds
    .map((id) => {
      return prots.find((prot) => {
        return prot.id === id;
      });
    })
    .filter((prot) => {
      return typeof prot !== "undefined";
    }) as IProtRow[];
}
